import { Fragment, useEffect, useState } from 'react';
import {
  fetchAssetsByTicketId,
  getAssetsByTicketIdLoadingSubject,
  getAssetsByTicketIdDrawerSubject,
  getAssetsByTicketIdLoadingDrawerSubject,
  getAssetsByTicketIdSubject
} from '../../../store/Asset';
import {
  fetchPAByTicketId,
  getPAByTicketIdLoadingSubject,
  getPAByTicketIdDrawerSubject,
  getPAByTicketIdLoadingDrawerSubject,
  getPAByTicketIdSubject
} from '../../../store/ProvisionalAsset';
import {
  fetchRPMAcctsByTicketId,
  getRpmAcctByTicketIdLoadingSubject,
  getRpmAcctByTicketIdDrawerSubject,
  getRpmAcctByTicketIdLoadingDrawerSubject,
  getRpmAcctByTicketIdSubject
} from '../../../store/RpmAccount';
import { EntityType, SearchResult } from '../../../store/Search';
import { createAssociations, removeAssociation, Ticket, addContractReferences } from '../../../store/Ticket';
import { AssetMiniTable } from '../../Compounds/Asset/AssetMiniTable';
import { PageCard } from '../../Compounds/CardWithTitle';
import { ProvisionalAssetMiniTable } from '../../Compounds/ProvisionalAsset/ProvisionalAssetMiniTable';
import { RPMMiniTable } from '../../Compounds/RPM/RPMMiniTable';
import { SearchBox } from '../../Compounds/Search/Search';
import { fetchSupplierMapsBySFDCSupplierId } from '../../../store/SupplierMap';
import { fetchTsdMaps } from '../../../store/TsdMap';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { isData, isObserver } from '../../../store/User';
import { Icon } from '../../Atoms/Icon';
import { TextSpan } from '../../Atoms/Text';
import { useSnackbar } from '../../../Context/SnackbarContext';

export interface ReferencesParams {
  ticket: Ticket;
  drawer?: boolean;
}

interface AddContractReferencesProps {
  callback: () => Promise<void>;
  ticketId: string;
  show: boolean;
}

const AddContractReferences = ({ callback, ticketId, show }: AddContractReferencesProps) => {
  const { setSnack } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const handleAddClick = async () => {
    setIsSubmitting(true);
    const { data } = await addContractReferences(ticketId);
    if (!data) {
      setIsSubmitting(false);
      return;
    }
    if (data.assetCount || data.rpmAccountCount) callback();
    setSnack({
      message: `${data.assetCount} Asset${data.assetCount !== 1 ? 's' : ''} and ${data.rpmAccountCount} RPM Account reference${data.rpmAccountCount !== 1 ? 's' : ''} added.`,
      type: 'success',
      open: true
    });
    setIsSubmitting(false);
  };

  return !show || isObserver() || isData() ? (
    <></>
  ) : (
    <button
      className="flex items-center space-x-2 mx-auto relative"
      disabled={isSubmitting}
      onClick={handleAddClick}
      data-cy="add-contract-references"
      data-testid="add-contract-references">
      <Icon type="plus" />
      <TextSpan
        size="sm14"
        color="indigo">
        Add contract references
      </TextSpan>
      {isSubmitting && (
        <TextSpan
          size="sm14"
          className="absolute w-max left-full"
          color="indigo">
          <ComponentSpinner label="Loading data..." />
        </TextSpan>
      )}
    </button>
  );
};

export const References = ({ ticket, drawer = false }: ReferencesParams) => {
  const [rpmFilters, setRpmFilters] = useState<Record<string, string>>();
  const [componentReady, setComponentReady] = useState<boolean>(false);
  const [currentReferences, setCurrentReferences] = useState<{ [key: string]: number }>();

  const handleFetchFiltersForRpm = async () => {
    await fetchTsdMaps();
    const supplierMaps = await fetchSupplierMapsBySFDCSupplierId(ticket.supplier);

    setRpmFilters({
      supplierId: supplierMaps?.[0]?.supplier_id ?? ''
    });
  };

  const handleFetchCurrentReferences = async () => {
    const pa = await fetchPAByTicketId(ticket.id, drawer)();
    const assets = await fetchAssetsByTicketId(ticket.id, drawer)();
    const rpmAccts = await fetchRPMAcctsByTicketId(ticket.id, drawer)();
    setCurrentReferences({
      provisional_asset: pa?.totalCount || 0,
      assets: assets?.totalCount || 0,
      rmpAccount: rpmAccts?.totalCount || 0
    });
  };

  const setUpComponent = async () => {
    await handleFetchFiltersForRpm();
    setComponentReady(true);
  };

  const paCardActions = () => {
    const handleAssetSelect = async (result: SearchResult) => {
      await createAssociations([
        {
          body: {
            entityId: result.id,
            ticketId: ticket.id
          },
          type: 'provisional_asset'
        }
      ]);
      fetchPAByTicketId(ticket.id, drawer)();
      setCurrentReferences((prevRefs) => ({
        ...prevRefs,
        provisional_asset: (prevRefs?.provisional_asset ?? 0) + 1
      }));
    };

    if (isObserver() || isData()) return <></>;

    const filters: Record<string, string> = {
      noTicketId: ticket.id
    };
    if (ticket.supplierName) filters.supplierId = ticket.supplier;
    return (
      <SearchBox
        placeholder="Add reference"
        handleSelect={handleAssetSelect}
        clearOnSuccess
        searchEntityType={EntityType.PROVISIONAL_ASSET}
        filters={filters}
      />
    );
  };

  const assetCardActions = () => {
    const handleAssetSelect = async (result: SearchResult) => {
      await createAssociations([
        {
          body: {
            entityId: result.id,
            ticketId: ticket.id
          },
          type: 'asset'
        }
      ]);
      fetchAssetsByTicketId(ticket.id, drawer)();
      setCurrentReferences((prevRefs) => ({
        ...prevRefs,
        assets: (prevRefs?.assets ?? 0) + 1
      }));
    };

    if (isObserver() || isData()) return <></>;

    const filters: Record<string, string> = {
      noTicketId: ticket.id
    };
    if (ticket.supplierName) filters.supplierId = ticket.supplier;
    return (
      <SearchBox
        placeholder="Add reference"
        handleSelect={handleAssetSelect}
        clearOnSuccess
        searchEntityType={EntityType.ASSET}
        filters={filters}
      />
    );
  };

  const rpmAcctCardActions = () => {
    const handleAssetSelect = async (result: SearchResult) => {
      await createAssociations([
        {
          body: {
            entityId: result.id,
            ticketId: ticket.id
          },
          type: 'rmpAccount'
        }
      ]);
      fetchRPMAcctsByTicketId(ticket.id, drawer)();
      setCurrentReferences((prevRefs) => ({
        ...prevRefs,
        rmpAccount: (prevRefs?.rmpAccount ?? 0) + 1
      }));
    };

    if (isObserver() || isData()) return <></>;

    return (
      <SearchBox
        placeholder="Add reference"
        handleSelect={handleAssetSelect}
        clearOnSuccess
        searchEntityType={EntityType.ACCOUNT}
        filters={{
          ...rpmFilters,
          noTicketId: ticket.id
        }}
      />
    );
  };

  useEffect(() => {
    const assetsSub = (drawer ? getAssetsByTicketIdDrawerSubject() : getAssetsByTicketIdSubject()).subscribe((a) => {
      setCurrentReferences((prevRefs) => ({
        ...prevRefs,
        assets: a.totalCount || 0
      }));
    });

    const paSub = (drawer ? getPAByTicketIdDrawerSubject() : getPAByTicketIdSubject()).subscribe((pa) => {
      setCurrentReferences((prevRefs) => ({
        ...prevRefs,
        provisional_asset: pa.totalCount || 0
      }));
    });

    const rpmSub = (drawer ? getRpmAcctByTicketIdDrawerSubject() : getRpmAcctByTicketIdSubject()).subscribe((rpm) => {
      setCurrentReferences((prevRefs) => ({
        ...prevRefs,
        rmpAccount: rpm.totalCount || 0
      }));
    });
    return () => {
      if (assetsSub) assetsSub.unsubscribe();
      if (paSub) paSub.unsubscribe();
      if (rpmSub) rpmSub.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (ticket && ticket.id) setUpComponent();
  }, [ticket, ticket.id]);

  return (
    <div className="space-y-6">
      {!componentReady && (
        <PageCard title="References">
          <ComponentSpinner
            sx={{ display: 'flex' }}
            label="Loading References"
          />
        </PageCard>
      )}
      {componentReady && (
        <Fragment>
          <PageCard
            title={`Assets (${currentReferences?.assets ?? 0})`}
            cardActions={assetCardActions()}>
            <AssetMiniTable
              id={ticket.id}
              fetchFunction={(id) => fetchAssetsByTicketId(id, drawer)}
              dataSubject={drawer ? getAssetsByTicketIdDrawerSubject : getAssetsByTicketIdSubject}
              loadingSubject={drawer ? getAssetsByTicketIdLoadingDrawerSubject : getAssetsByTicketIdLoadingSubject}
              removeFunction={async (rowId) => {
                await removeAssociation(rowId, ticket.id, 'asset');
                setCurrentReferences((prevRefs) => ({
                  ...prevRefs,
                  assets: (prevRefs?.assets || 0) - 1
                }));
              }}
              showRemove={true}
              type="Reference"
              drawer={drawer}
            />
          </PageCard>
          <PageCard
            title={`Provisional Assets (${currentReferences?.provisional_asset ?? 0})`}
            cardActions={paCardActions()}>
            <ProvisionalAssetMiniTable
              id={ticket.id}
              fetchFunction={(id) => fetchPAByTicketId(id, drawer)}
              dataSubject={drawer ? getPAByTicketIdDrawerSubject : getPAByTicketIdSubject}
              loadingSubject={drawer ? getPAByTicketIdLoadingDrawerSubject : getPAByTicketIdLoadingSubject}
              showRemove={true}
              removeFunction={async (rowId) => {
                await removeAssociation(rowId, ticket.id, 'provisional_asset');
                setCurrentReferences((prevRefs) => ({
                  ...prevRefs,
                  provisional_asset: (prevRefs?.provisional_asset || 0) - 1
                }));
              }}
              type="Reference"
              drawer={drawer}
            />
          </PageCard>
          <PageCard
            title={`RPM Accounts (${currentReferences?.rmpAccount ?? 0})`}
            cardActions={rpmAcctCardActions()}>
            <RPMMiniTable
              id={ticket.id}
              fetchFunction={(id) => fetchRPMAcctsByTicketId(id, drawer)}
              dataSubject={drawer ? getRpmAcctByTicketIdDrawerSubject : getRpmAcctByTicketIdSubject}
              loadingSubject={drawer ? getRpmAcctByTicketIdLoadingDrawerSubject : getRpmAcctByTicketIdLoadingSubject}
              showRemove={true}
              removeFunction={async (rowId) => {
                await removeAssociation(rowId, ticket.id, 'rmpAccount');
                setCurrentReferences((prevRefs) => ({
                  ...prevRefs,
                  rmpAccount: (prevRefs?.rmpAccount || 0) - 1
                }));
              }}
              type="Reference"
              drawer={drawer}
            />
          </PageCard>
          <AddContractReferences
            callback={handleFetchCurrentReferences}
            ticketId={ticket.id}
            show={!!currentReferences && currentReferences?.assets > 0}
          />
        </Fragment>
      )}
    </div>
  );
};
